import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { number, object, ObjectSchema } from 'yup';
import ClientOnboardingApi from '../../../../apis/ClientOnboardingApi';
import FormikTextField from '../../../../components/FormikTextField/FormikTextField';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import PricingTable from '../../../../components/PricingTable/PricingTable';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { LicenseAndPricingDto } from '../../../../generated-types/licenseAndPricingDto';
import { LicenseTierDto } from '../../../../generated-types/licenseTierDto';
import { calculateTotal } from '../../../../lib/calculateTotal';
import useApi from '../../../../lib/useApi';
import CommonStepProps from '../../lib/CommonStepProps';

/**
 * A structure to represent the license and pricing form
 */
interface LicenseAndPricingForm {
  /**
   * The number of licenses the organization needs
   */
  licenseCount: number | string;
}

/**
 * The component to gather how many licenses the user wants and to present the pricing information to them.
 */
const LicenseAndPricingStep = ({
  isStepComplete,
  setStepComplete,
  moveToNextStep,
  showError,
  isMasterServiceAgreementStepComplete,
}: CommonStepProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [licenseTiers, setLicenseTiers] = useState<LicenseTierDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [initialValues, setInitialValues] = useState<LicenseAndPricingForm>({
    licenseCount: '',
  });
  const [minimum, setMinimum] = useState<number>(0);
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const snackbarContext = useContext(SnackbarContext);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  });

  /**
   * This use affect is primarily responsible for the initial load of information for
   *  when the user first visits this step
   */
  useEffect(() => {
    const load = async () => {
      const loadLicenseTiersPromise = clientOnboardingApi.GetLicenseTiers();

      // Load the previous information, if the step is complete
      let initialLicenseCount = 0;
      if (isStepComplete) {
        await clientOnboardingApi.GetLicenseAndPricing().then((l) => {
          setInitialValues(l);
          initialLicenseCount = l.licenseCount;
        });
      }

      const loadedLicenseTiers = await loadLicenseTiersPromise;
      setLicenseTiers(loadedLicenseTiers);
      setTotal(calculateTotal(initialLicenseCount, loadedLicenseTiers));

      // Load the minimum license count (used for public purchase)
      const min = await clientOnboardingApi.GetLicenseMinimumWhileOnboarding();
      setMinimum(min);
    };
    load()
      .catch(() => {
        snackbarContext.setAlert(
          <Alert severity="error" variant="filled">
            An error occurred while loading the license and pricing step
          </Alert>
        );
        snackbarContext.triggerSnackbar();
        showError();
      })
      .finally(() => setIsLoading(false));
  }, [clientOnboardingApi, isStepComplete, showError, snackbarContext]);

  /**
   * The schema for the formik form
   */
  const schema: ObjectSchema<LicenseAndPricingForm> = object({
    licenseCount: number()
      .min(
        minimum,
        `You must add at least ${minimum} licenses.${
          minimum > 2 ? ' If you are looking for less, please contact us.' : ''
        }`
      )
      .integer('Must be a whole number')
      .defined('Required'),
  });

  /**
   * The formik form. This also contains the code for submitting the form
   */
  const formik = useFormik<LicenseAndPricingForm>({
    initialValues: initialValues,
    validationSchema: isMasterServiceAgreementStepComplete ? undefined : schema,
    enableReinitialize: true,
    onSubmit: (val, { setSubmitting }) => {
      // Complete the step
      clientOnboardingApi
        .CompleteLicenseAndPricing(val as unknown as LicenseAndPricingDto)
        .then(() => {
          if (!isStepComplete) {
            setStepComplete();
            moveToNextStep();
          } else {
            snackbarContext.setAlert(
              <Alert variant="filled" severity="success">
                Successfully updated number of licenses
              </Alert>
            );
            snackbarContext.triggerSnackbar();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An error occurred while completing the license and pricing step.
              Please try again.
            </Alert>
          );
          snackbarContext.triggerSnackbar();
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  /**
   * Extra details to show to the user around licenses
   */
  const extraDetails = useMemo(
    () => [
      'Licenses provide unlimited training access',
      'Discounts for the more licenses you purchase',
      'More licenses can be added later, if needed',
      'Automatically renews each year. Can cancel anytime.',
      'Each member in your organization must have a license to access the training',
      'Licenses cannot be reused once the member has started the training',
    ],
    []
  );

  return (
    <Box position="relative">
      <Box
        display="flex"
        gap="2em"
        paddingBottom="1em"
        padding={isSm ? 'initial' : '.5em'}
        marginTop="1em"
        flexWrap={isSm ? 'nowrap' : 'wrap'}
        flexDirection={isSm ? 'row' : 'column-reverse'}
      >
        <Box
          width={isSm ? '50%' : '100%'}
          display="flex"
          flexDirection="column"
          gap="1em"
        >
          <Box>
            <Typography variant="h5" gutterBottom>
              License Details
            </Typography>
            <Typography marginBottom="1em">
              We are excited to provide your organization with licenses to
              access the S3G Security Training. Here a few things to know,
            </Typography>
            <Box display="flex" gap=".25em" flexDirection="column">
              {extraDetails.map((l, i) => (
                <Box key={i} display="flex" gap=".75em">
                  <CheckCircleOutlineRoundedIcon color="success" />
                  <Typography>{l}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Divider flexItem />
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Typography marginBottom="1.5em">
                Please enter the number of licenses you need for your
                organization.
              </Typography>
              <FormikTextField
                formik={formik}
                fullWidth
                label="Number of licenses"
                name="licenseCount"
                disabled={
                  formik.isSubmitting || isMasterServiceAgreementStepComplete
                }
                type="number"
                additionalOnChange={(e) => {
                  const updatedTotal = calculateTotal(
                    e.target.value as number,
                    licenseTiers
                  );
                  setTotal(updatedTotal);
                }}
              />
              <Box
                display="flex"
                gap=".5em"
                marginTop="1em"
                justifyContent="space-between"
              >
                <Typography fontWeight="bold" fontSize="1.25em">
                  Total
                </Typography>
                <Box display="flex" gap=".5em" alignItems="center">
                  <Typography fontWeight="bold" fontSize="1.25em">
                    {total && total > 0
                      ? `$${total.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '$0.00'}
                  </Typography>
                  <Typography fontWeight="200">per year</Typography>
                </Box>
              </Box>
              <Divider sx={{ marginY: '1.25em' }} />
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                gap="1em"
              >
                {!isMasterServiceAgreementStepComplete && (
                  <LoadingButton
                    loading={formik.isSubmitting}
                    type="submit"
                    variant={isStepComplete ? 'outlined' : 'contained'}
                  >
                    {isStepComplete ? 'Update' : 'Confirm'} Licenses
                  </LoadingButton>
                )}
                {isStepComplete && (
                  <Button onClick={moveToNextStep} variant="contained">
                    Next Step
                  </Button>
                )}
              </Box>
            </form>
          </Box>
        </Box>
        <Box width={isSm ? '50%' : '100%'}>
          <PricingTable licenseTiers={licenseTiers} />
        </Box>
      </Box>
      <LoadingCircle isLoading={isLoading} blurEdges />
    </Box>
  );
};

export default LicenseAndPricingStep;
